import React from "react";
import { Alert, Box, Button, Checkbox, CircularProgress, TextField, Typography } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import CustomButton from "../../../components/buttons/CustomButton";
import ColorPalette from "../../../colorpalette/ColorPalette";

/**
 * Passos do diálogo "Excluir paciente" (PatientDeleteDialog). Só apresentação:
 * o estado e as chamadas de API ficam no diálogo.
 * Especificação: docs/features/lgpd-eliminacao/README.md
 */

// [chave em counts, singular, plural]
const ERASED_ITEMS = [
    ["anamneses", "anamnese", "anamneses"],
    ["history", "atendimento no histórico", "atendimentos no histórico"],
    ["complaints", "queixa", "queixas"],
    ["cranial", "foto/avaliação craniana", "fotos/avaliações cranianas"],
    ["attachments", "anexo", "anexos"],
    ["documents", "documento", "documentos"],
    ["notes", "anotação", "anotações"],
];

const TEXT_SX = { color: "var(--color-text-dark)", lineHeight: 1.5 };

const LIGHT_BOX_SX = {
    border: `1px solid ${ColorPalette.border}`,
    borderRadius: "8px",
    backgroundColor: "#FAFAFA",
    padding: "12px 16px",
};

// Colunas lado a lado a partir de 600px (breakpoint sm); abaixo disso, uma coluna.
const TWO_COLUMNS_SX = {
    display: "grid",
    gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
    gap: 1.5,
    alignItems: "start",
};

const COLUMN_CARD_SX = { ...LIGHT_BOX_SX, padding: "10px 14px" };
// Coluna "apagado" em vermelho e "permanece" em azul: o contraste guia a leitura.
const ERASED_CARD_SX = { ...COLUMN_CARD_SX, borderColor: "#FFCDD2", backgroundColor: "#FFF8F8" };
const KEPT_CARD_SX = { ...COLUMN_CARD_SX, borderColor: ColorPalette.blueLight3, backgroundColor: "#F8FAFF" };

// Aceite como cartão clicável inteiro: caixa e texto centralizados, borda azul quando marcado.
// Reusado pelo diálogo "Encerrar conta" (components/company-closure).
export function ConsentCard({ checked, disabled, onChange, label }) {
    const toggle = () => {
        if (!disabled) onChange(!checked);
    };

    return (
        <Box
            onClick={toggle}
            sx={{
                ...LIGHT_BOX_SX,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                minWidth: 0,
                cursor: disabled ? "default" : "pointer",
                userSelect: "none",
                borderColor: checked ? ColorPalette.blueMain : ColorPalette.border,
                backgroundColor: checked ? "#F8FAFF" : "#FAFAFA",
                transition: "border-color 120ms, background-color 120ms",
                "&:hover": { borderColor: disabled ? undefined : ColorPalette.blueLight2 },
            }}
        >
            <Checkbox
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                sx={{ padding: "4px", flexShrink: 0 }}
            />
            <Typography variant="body2" sx={{ ...TEXT_SX, fontWeight: checked ? 600 : 400, minWidth: 0 }}>
                {label}
            </Typography>
        </Box>
    );
}

export function toCount(value) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : 0;
}

function pluralize(count, singular, plural) {
    return `${count} ${count === 1 ? singular : plural}`;
}

function StepTitle({ children }) {
    return (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "var(--color-text-dark)" }}>
            {children}
        </Typography>
    );
}

function ColumnTitle({ children, color = "var(--color-text-dark)" }) {
    return (
        <Typography variant="body2" sx={{ fontWeight: 600, color, marginBottom: "4px" }}>
            {children}
        </Typography>
    );
}

function ItemList({ items }) {
    return (
        <Box component="ul" sx={{ margin: 0, paddingLeft: "18px" }}>
            {items.map((item) => (
                <Typography key={item} component="li" variant="body2" sx={{ ...TEXT_SX, overflowWrap: "anywhere" }}>
                    {item}
                </Typography>
            ))}
        </Box>
    );
}

// Identifica o paciente em todos os passos: deixa claro QUEM será excluído.
export function DeletePatientIdentity({ patientId, patientName }) {
    return (
        <Box sx={{ ...LIGHT_BOX_SX, display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: ColorPalette.error,
                    backgroundColor: ColorPalette.errorLight,
                }}
            >
                <PersonOutlineOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "var(--color-text-dark)", overflowWrap: "anywhere", lineHeight: 1.3 }}>
                    {patientName || "Paciente sem nome"}
                </Typography>
                <Typography variant="caption" sx={{ color: "var(--color-text-light)" }}>
                    {`Paciente #${patientId}`}
                </Typography>
            </Box>
        </Box>
    );
}

export function DeletePreviewStep({ patientId, loading, loadError, preview, onRetry, onSwitchToAnonymize }) {
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (loadError || !preview) {
        return (
            <Alert
                severity="error"
                action={<Button color="inherit" size="small" onClick={onRetry}>Tentar novamente</Button>}
            >
                {loadError || "Não foi possível carregar o resumo da exclusão."}
            </Alert>
        );
    }

    const counts = preview.counts || {};
    const warnings = Array.isArray(preview.warnings) ? preview.warnings : [];
    const blockers = Array.isArray(preview.blockers) ? preview.blockers : [];
    const pastAppointments = toCount(counts.pastAppointments);
    const futureAppointments = toCount(counts.futureAppointments);

    const erased = ERASED_ITEMS
        .filter(([key]) => toCount(counts[key]) > 0)
        .map(([key, singular, plural]) => pluralize(toCount(counts[key]), singular, plural));
    if (!preview.alreadyAnonymized) {
        erased.unshift("dados pessoais (nome, CPF, contatos e endereço)");
    }

    const kept = ["faturas", "lançamentos", "notas fiscais"];
    if (pastAppointments > 0) {
        kept.unshift(pluralize(pastAppointments, "agendamento passado", "agendamentos passados"));
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <StepTitle>O que vai acontecer</StepTitle>

            {preview.alreadyDeleted && (
                <Alert severity="info">Este paciente já foi excluído.</Alert>
            )}

            {/* Duas colunas no desktop (apagado | permanece); uma no celular. */}
            <Box sx={TWO_COLUMNS_SX}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                    <Box sx={ERASED_CARD_SX}>
                        <ColumnTitle color="#C62828">Será apagado da Gabriela</ColumnTitle>
                        {erased.length > 0 ? (
                            <ItemList items={erased} />
                        ) : (
                            <Typography variant="body2" sx={TEXT_SX}>Nenhum registro clínico cadastrado.</Typography>
                        )}
                    </Box>
                    {futureAppointments > 0 && (
                        <Typography variant="body2" sx={TEXT_SX}>
                            {`${pluralize(futureAppointments, "agendamento futuro será cancelado", "agendamentos futuros serão cancelados")}.`}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ ...KEPT_CARD_SX, minWidth: 0 }}>
                    <ColumnTitle color={ColorPalette.blueDark}>Permanecem na Gabriela</ColumnTitle>
                    <Typography variant="caption" sx={{ display: "block", color: "var(--color-text-light)", lineHeight: 1.4, marginBottom: "4px" }}>
                        {`Ligados a "Paciente anonimizado #${patientId}":`}
                    </Typography>
                    <ItemList items={kept} />
                </Box>
            </Box>

            {warnings.map((warning, index) => (
                <Alert key={`warning-${warning.code || index}`} severity="warning">{warning.message}</Alert>
            ))}
            {blockers.map((blocker, index) => (
                <Alert key={`blocker-${blocker.code || index}`} severity="error">{blocker.message}</Alert>
            ))}

            {!preview.alreadyAnonymized && (
                <Box sx={{ ...LIGHT_BOX_SX, display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
                    <Typography variant="body2" sx={{ color: "var(--color-text-dark)", flex: "1 1 180px", lineHeight: 1.5 }}>
                        Se a intenção é apenas remover os dados pessoais, prefira anonimizar.
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        disableElevation
                        onClick={onSwitchToAnonymize}
                        sx={{ textTransform: "none", flexShrink: 0, whiteSpace: "nowrap", borderRadius: "8px" }}
                    >
                        Anonimizar em vez disso
                    </Button>
                </Box>
            )}
        </Box>
    );
}

export function DeleteExportStep({ exporting, exported, onExport }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <StepTitle>Guarde os dados exportados</StepTitle>

            {/* Desktop: explicação à esquerda, ação e status à direita. */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) 240px" },
                    gap: { xs: 1.5, sm: 2 },
                    alignItems: "center",
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}>
                    <Typography variant="body2" sx={TEXT_SX}>
                        O arquivo ZIP exportado é a cópia completa que fica com você: cadastro, histórico,
                        anamneses, documentos e anexos. Depois da exclusão a Gabriela não terá mais esses dados e não
                        conseguirá gerar este arquivo de novo.
                    </Typography>
                    <Alert severity="info">
                        Se a sua atividade é da área da saúde, a lei exige guardar esses registros por no mínimo
                        20 anos (Lei 13.787/2018). Essa responsabilidade passa a ser sua, com base neste arquivo exportado.
                    </Alert>
                </Box>

                <Box sx={{ ...COLUMN_CARD_SX, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1.5, minWidth: 0 }}>
                    <CustomButton
                        label="Exportar dados agora"
                        style="primary"
                        variant="contained"
                        icon={<FileDownloadOutlinedIcon fontSize="small" />}
                        loading={exporting}
                        enabled={!exporting}
                        onClick={onExport}
                    />

                    {exported ? (
                        <Alert severity="success" sx={{ width: "100%", boxSizing: "border-box" }}>
                            Exportação concluída. Guarde o arquivo em local seguro.
                        </Alert>
                    ) : (
                        <Typography variant="body2" sx={{ color: "var(--color-text-light)", lineHeight: 1.5 }}>
                            Para continuar, exporte os dados deste paciente.
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export function DeleteConfirmStep({
    patientName,
    custodyChecked,
    irreversibleChecked,
    typedName,
    disabled,
    onCustodyChange,
    onIrreversibleChange,
    onTypedNameChange,
}) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <StepTitle>Confirmação</StepTitle>

            {/* Cada aceite é um cartão clicável, um embaixo do outro; a borda fica azul quando marcado. */}
            <ConsentCard
                checked={custodyChecked}
                disabled={disabled}
                onChange={onCustodyChange}
                label="Assumo a guarda dos dados exportados e a responsabilidade legal por eles"
            />
            <ConsentCard
                checked={irreversibleChecked}
                disabled={disabled}
                onChange={onIrreversibleChange}
                label="Entendo que a exclusão é irreversível"
            />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 0.5 }}>
                <Typography variant="body2" sx={TEXT_SX}>
                    Para liberar o botão de exclusão, digite o nome do paciente exatamente como está no cadastro.
                </Typography>
                <Typography variant="body2" sx={{ ...TEXT_SX, overflowWrap: "anywhere" }}>
                    Nome esperado: <strong>{patientName}</strong>
                </Typography>
                <TextField
                    size="small"
                    label="Nome do paciente"
                    placeholder={patientName}
                    value={typedName}
                    onChange={(e) => onTypedNameChange(e.target.value)}
                    fullWidth
                    disabled={disabled}
                    InputLabelProps={{ shrink: true }}
                />
            </Box>
        </Box>
    );
}
